import React, { useState, useEffect } from 'react';
import { Instagram, Loader, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db } from '../utils/firebase';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { FooterContent, FooterLink } from '../types';
import { DEFAULT_FOOTER_CONTENT } from '../constants';
import ShareModal from './common/ShareModal';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type SubscribeStatus = 'idle' | 'loading' | 'success' | 'error' | 'invalid_email' | 'already_subscribed';

const isExternal = (url: string) => /^https?:\/\//i.test(url);

const FooterNavLink: React.FC<{ link: FooterLink; className: string }> = ({ link, className }) =>
  isExternal(link.url) ? (
    <a href={link.url} target="_blank" rel="noopener noreferrer" className={className}>{link.label}</a>
  ) : (
    <Link to={link.url} className={className}>{link.label}</Link>
  );

const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<SubscribeStatus>('idle');
  const [content, setContent] = useState<FooterContent>(DEFAULT_FOOTER_CONTENT);
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    const fetchContent = async () => {
      if (!db) return;
      try {
        const docSnap = await getDoc(doc(db, 'content', 'footer'));
        if (docSnap.exists()) {
          setContent({ ...DEFAULT_FOOTER_CONTENT, ...(docSnap.data() as Partial<FooterContent>) });
        }
      } catch (error) {
        console.error('Error fetching footer content:', error);
      }
    };
    fetchContent();
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    // Reset error states when user starts typing
    if (status === 'error' || status === 'invalid_email' || status === 'already_subscribed') {
      setStatus('idle');
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    // Normalize email
    const normalizedEmail = email.toLowerCase().trim();

    // Validate email format
    if (!normalizedEmail || !EMAIL_REGEX.test(normalizedEmail)) {
      setStatus('invalid_email');
      return;
    }

    // Check if Firebase is configured
    if (!db) {
      console.error('Firebase not configured');
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      // Check for duplicate email
      const existingQuery = query(
        collection(db, 'subscribers'),
        where('email', '==', normalizedEmail)
      );
      const existingDocs = await getDocs(existingQuery);

      if (!existingDocs.empty) {
        setStatus('already_subscribed');
        return;
      }

      // Add new subscriber
      await addDoc(collection(db, 'subscribers'), {
        email: normalizedEmail,
        createdAt: serverTimestamp(),
        source: 'footer'
      });
      setStatus('success');
      setEmail('');
    } catch (error) {
      console.error("Error subscribing:", error);
      setStatus('error');
    }
  };

  return (
    <footer className="bg-[#111] text-white pt-20 pb-10">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Newsletter */}
          <div className="md:col-span-3 lg:col-span-3">
            <h4 className="font-heading font-bold text-lg tracking-widest mb-6 uppercase">{content.newsletterHeading}</h4>
            <p className="font-body text-gray-400 text-sm mb-4">
              {content.newsletterText}
            </p>
            {status === 'success' ? (
                <div
                  className="bg-green-900/20 border border-green-900 p-4 flex items-center gap-3 text-green-500"
                  role="status"
                  aria-live="polite"
                >
                    <Check size={20} aria-hidden="true" />
                    <span className="font-heading font-bold text-xs tracking-wider">{content.newsletterSuccessMessage}</span>
                </div>
            ) : (
                <form onSubmit={handleSubscribe} className="flex flex-col gap-2" aria-label="Newsletter subscription">
                <div>
                  <label htmlFor="footer-email" className="sr-only">Email address</label>
                  <input
                      id="footer-email"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="ENTER EMAIL"
                      disabled={status === 'loading'}
                      aria-describedby={status === 'error' || status === 'invalid_email' || status === 'already_subscribed' ? 'subscribe-error' : undefined}
                      aria-invalid={status === 'error' || status === 'invalid_email' || status === 'already_subscribed'}
                      className="w-full bg-transparent border border-gray-600 px-4 py-3 text-sm tracking-wide focus:border-white outline-none placeholder:text-gray-500 disabled:opacity-50"
                  />
                </div>
                <button
                    type="submit"
                    disabled={status === 'loading'}
                    aria-busy={status === 'loading'}
                    className="bg-white text-black font-heading font-bold text-sm py-3 tracking-widest hover:bg-gray-200 transition-colors uppercase disabled:opacity-70 flex items-center justify-center"
                >
                    {status === 'loading' ? <Loader className="animate-spin" size={16} aria-hidden="true" /> : 'NOTIFY ME'}
                </button>
                <div id="subscribe-error" aria-live="polite" className="min-h-[1.25rem]">
                  {status === 'error' && <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>}
                  {status === 'invalid_email' && <p className="text-red-500 text-xs">Please enter a valid email address.</p>}
                  {status === 'already_subscribed' && <p className="text-amber-500 text-xs">This email is already subscribed.</p>}
                </div>
                </form>
            )}
          </div>

          {/* Link Columns (managed in Admin > Site Content > Footer) */}
          {content.columns.map((column, index) => (
            <div key={index} className="md:col-span-3 lg:col-span-3">
              <h4 className="font-heading font-bold text-lg tracking-widest mb-6 uppercase">{column.title}</h4>
              <ul className="space-y-3 font-body text-sm text-gray-400">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <FooterNavLink link={link} className="hover:text-white transition-colors" />
                  </li>
                ))}
              </ul>
              {index === content.columns.length - 1 && content.instagramUrl && (
                <div className="flex gap-4 mt-8">
                  <a href={content.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-400 hover:text-white transition-colors"><Instagram size={20} /></a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-gray-500 font-body">
            &copy; {new Date().getFullYear()} {content.copyrightName}. ALL RIGHTS RESERVED.
          </p>
          <div className="flex gap-6 text-xs text-gray-500 font-body">
            {content.bottomLinks.map((link, index) => (
              <FooterNavLink key={index} link={link} className="hover:text-white" />
            ))}
            <button
              onClick={() => setIsShareOpen(true)}
              className="hover:text-white uppercase"
            >
              Share
            </button>
          </div>
        </div>
      </div>

      <ShareModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} />
    </footer>
  );
};

export default Footer;